import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/inventory/equipment-types")({
  component: () => (
    <RequireCapability cap="inventory:manage">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const utils = trpc.useUtils();
  const [newName, setNewName] = useState("");

  const { data: types, isLoading } = trpc.equipment.types.list.useQuery();

  const createMutation = trpc.equipment.types.create.useMutation({
    onSuccess: () => {
      toast.success("Type created");
      utils.equipment.types.list.invalidate();
      setNewName("");
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`);
    },
  });

  const updateMutation = trpc.equipment.types.update.useMutation({
    onSuccess: () => {
      utils.equipment.types.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Failed to update: ${err.message}`);
    },
  });

  const deleteMutation = trpc.equipment.types.delete.useMutation({
    onSuccess: () => {
      toast.success("Type deleted");
      utils.equipment.types.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Failed to delete: ${err.message}`);
    },
  });

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName });
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h2 className="text-lg font-medium">Equipment Types</h2>
        <p className="text-sm text-muted-foreground">
          Equipment types categorize your equipment (e.g., Bioreactor, Centrifuge, Scale).
        </p>
      </div>

      <div className="flex gap-2 max-w-md">
        <Input
          placeholder="New type name..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <Button
          onClick={handleCreate}
          disabled={createMutation.isPending || !newName.trim()}
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {types && types.length > 0 ? (
        <div className="space-y-2 max-w-md">
          {types.map((type) => (
            <div
              key={type.id}
              className="flex items-center gap-2 p-2 border border-olive-6"
            >
              <Input
                defaultValue={type.name}
                onBlur={(e) => {
                  if (e.target.value !== type.name) {
                    updateMutation.mutate({ id: type.id, name: e.target.value });
                  }
                }}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteMutation.mutate(type.id)}
              >
                <Trash2Icon className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No equipment types defined.</p>
      )}
    </div>
  );
}
